/**
 * Shared types for the messaging system
 * Extracted from MessagesModal for better modularity
 */

import { getRuntimeSocketUrl } from '@/lib/publicRuntimeConfig';

// Thread member with permissions
export interface ThreadMember {
  user_id: number;
  display_name: string;
  email: string;
  avatar_url?: string;
  role: 'admin' | 'member';
  last_activity_at?: string;
  show_when_online?: boolean;
  is_online?: boolean; // Current online status
  permissions: {
    can_read: boolean;
    can_send_messages: boolean;
    can_send_images: boolean;
    can_delete_own_messages: boolean;
    can_delete_any_messages: boolean;
    can_manage_members: boolean;
    can_manage_thread: boolean;
    can_leave?: boolean;
  };
  joined_at: string;
}

// Permission presets for quick member permission assignment
export interface PermissionPreset {
  label: string;
  description: string;
  permissions: Partial<ThreadMember['permissions']>;
}

export const PERMISSION_PRESETS: Record<string, PermissionPreset> = {
  full_access: {
    label: 'Full Access',
    description: 'Can send messages, images, and manage content',
    permissions: {
      can_read: true,
      can_send_messages: true,
      can_send_images: true,
      can_delete_own_messages: true,
      can_delete_any_messages: false,
      can_manage_members: false,
      can_manage_thread: false,
    },
  },
  can_post: {
    label: 'Can Post',
    description: 'Can send messages and images',
    permissions: {
      can_read: true,
      can_send_messages: true,
      can_send_images: true,
      can_delete_own_messages: true,
      can_delete_any_messages: false,
      can_manage_members: false,
      can_manage_thread: false,
    },
  },
  can_comment: {
    label: 'Can Comment',
    description: 'Can send text messages only, no images',
    permissions: {
      can_read: true,
      can_send_messages: true,
      can_send_images: false,
      can_delete_own_messages: true,
      can_delete_any_messages: false,
      can_manage_members: false,
      can_manage_thread: false,
    },
  },
  read_only: {
    label: 'Read Only',
    description: 'Can only view messages, cannot send',
    permissions: {
      can_read: true,
      can_send_messages: false,
      can_send_images: false,
      can_delete_own_messages: false,
      can_delete_any_messages: false,
      can_manage_members: false,
      can_manage_thread: false,
    },
  },
};

// Pinned mention interface
export interface PinnedMention {
  message_id: string;
  content: string;
  message_preview: string;
  sender_name: string;
  sender_avatar?: string;
  timestamp: string;
  dismissed: boolean;
}

// Client thread
export interface ClientThread {
  _id: string;
  id: string; // Alias for thread_id
  thread_id: string;
  client_id: number;
  thread_name: string;
  title: string; // Alias for thread_name (for convenience)
  thread_description?: string;
  description?: string; // Alias for thread_description
  avatar_url?: string;
  thread_type: 'direct' | 'group' | 'job' | 'schedule' | 'system' | 'client' | 'total_operations' | 'team' | string;
  members: ThreadMember[];
  member_count: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  last_message_at?: string;
  last_message_preview?: string;
  last_message_sender?: string;
  is_active: boolean;
  is_muted?: boolean; // Whether notifications are muted
  unread_count?: number;
  my_role?: string;
  my_permissions?: Record<string, boolean>;
  is_system_thread?: boolean; // True for system notification threads
  job_id?: number; // For job threads
  job_number?: string; // For job threads
  job_title?: string; // For job threads
  contact_name?: string; // For job threads - contact/customer name
  contact_location?: string; // For job threads - contact location/address
  schedule_id?: number; // For schedule threads
  settings?: {
    is_fixed_membership?: boolean;
    allow_member_invites?: boolean;
    auto_sync_module?: string;
    mute_notifications?: boolean;
    is_system_thread?: boolean; // System thread setting flag
  };
  // Team thread fields (populated by teamThreadBridge)
  team_id?: string; // UUID of the team
  team_name?: string; // Human-friendly team name when available
  solution_key?: string; // Solution key for solution/team threads
  team_icon?: string; // Lucide icon name (e.g. 'shield', 'rocket')
  team_color?: string; // Hex color (e.g. '#3b82f6')
  is_team_thread?: boolean; // True for team threads
  // Cross-client fields (when fetching threads from multiple clients)
  client_name?: string;
  client_logo?: string;
  // System thread specific
  system_client_logo?: string; // Logo from client_id=1 for system threads
}

// Attachment
export interface Attachment {
  id: string;
  filename?: string;
  originalFilename: string;
  url: string;
  contentType?: string;
  mimeType?: string; // Alias for contentType
  size: number;
  isImage: boolean;
  isVideo: boolean;
  thumbnailUrl?: string;
  duration?: number; // For voice notes/videos
}

// Thread message
export interface ThreadMessage {
  _id: string;
  id: string;
  content: string;
  content_type?: 'text' | 'image' | 'video' | 'file' | 'voice' | 'location';
  thread_id: string;
  sender_id: number; // Alias for sender_user_id
  sender_user_id: number;
  sender_name: string;
  sender_email?: string;
  sender_avatar?: string;
  message_type: string;
  attachments?: Attachment[];
  timestamp: string;
  created_at: string;
  read_by: Array<{ user_id: number; read_at: string; user_name?: string }>;
  edited?: boolean;
  deleted?: boolean;
  reply_to_id?: string; // For convenience
  reply_to?: {
    message_id: string;
    content: string;
    message_preview: string;
    sender_name: string;
  };
  mentions?: number[]; // Array of mentioned user_ids
  reactions?: Array<{
    user_id: number;
    user_name: string;
    emoji: string;
    created_at: string;
  }>;
  location?: {
    latitude: number;
    longitude: number;
    accuracy?: number;
    address?: string;
  };
  // API message fields
  is_api_message?: boolean;
  is_system_message?: boolean;
  metadata?: {
    sent_via?: string;
    job_id?: string;
    job_title?: string;
    [key: string]: any;
  };
  // Offline queue fields
  offline_id?: string;
  pending?: boolean;
  failed?: boolean;
  temp_id?: string; // Temporary ID for matching optimistic messages with server responses
  // Additional offline metadata (added by offlineMessages service)
  _offline?: boolean;
  _offlineId?: string;
  _offlineStatus?: 'pending' | 'sending' | 'sent' | 'failed';
  _offlineError?: string;
}

// Eligible member for thread creation/adding
export interface EligibleMember {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  display_name: string;
  modules: string[];
}

// System message
export interface SystemMessage {
  id: string;
  content: string;
  subject?: string;
  sender_id: number;
  sender_name: string;
  created_at: string;
  read: boolean;
  message_type: string;
  priority?: string;
  metadata?: Record<string, any>;
}

// Incoming call interface for voice/video calls
export interface IncomingCall {
  callId: string;
  callerId: number;
  callerName: string;
  callerAvatar?: string;
  threadId: string;
  threadName: string;
  callType: 'voice' | 'video';
  timestamp: string;
}

// User feature settings for messaging
export interface UserFeatureSettings {
  message_centre: boolean;
  job_threads: boolean;
  client_thread: boolean;
  schedule_thread: boolean;
  show_when_online: boolean;
  voice_calls: boolean;
  video_calls: boolean;
  smart_task_field_worker: boolean;
  show_last_known_location: boolean;
  my_tasks: boolean;
  total_assistant: boolean;
}

// Typing user indicator
export interface TypingUser {
  userId: number;
  userName: string;
  timestamp: number;
}

// Quick reaction emojis for messages
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '🎉', '🔥', '👏', '🙏', '✅', '👀', '🤔', '😢'];

// View modes for the messages modal
export type ViewMode = 'list' | 'thread' | 'threads_list' | 'system_messages' | 'thread_conversation' | 'create_thread' | 'thread_settings' | 'add_members';

// WebSocket connection status
export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

// Message send status for offline queue
export type MessageStatus = 'pending' | 'sending' | 'sent' | 'failed';

// Offline message for queue
export interface OfflineMessage {
  id: string;
  thread_id: string;
  content: string;
  message_type: string;
  attachments?: Attachment[];
  reply_to?: {
    message_id: string;
    content: string;
    sender_name: string;
  };
  mentions?: number[];
  location?: {
    latitude: number;
    longitude: number;
    accuracy?: number;
    address?: string;
  };
  created_at: string;
  status: MessageStatus;
  retry_count: number;
  last_retry_at?: string;
  error?: string;
  // For optimistic UI
  temp_id: string;
  sender_user_id: number;
  sender_name: string;
}

// Constants
// @deprecated Use getWebSocketUrl() from '@/lib/platformUtils' instead - static URLs don't work correctly in Capacitor
// This constant is kept for backward compatibility but should not be used
export const WEBSOCKET_URL = getRuntimeSocketUrl() || 'wss://websocket.totalaccess.co.za';
export const SYSTEM_MESSAGES_API = '/api/v1/system-messages';

// Cloud Storage - unified storage for all messaging and email attachments
// All uploads now go through /api/v1/cloud-storage which enforces client quotas
export const CLOUD_STORAGE_API_PATH = '/api/v1/cloud-storage/attachments/upload';
export const CLOUD_STORAGE_URL = process.env.NEXT_PUBLIC_CLOUD_STORAGE_URL || 'https://cloud-storage.totalaccess.co.za';

// Legacy constants - DEPRECATED and redirected to cloud-storage
// @deprecated Use CLOUD_STORAGE_URL and uploadToThreadStorage from '@/lib/threadStorageUpload' instead
// Thread-storage no longer exists - all references should use cloud-storage
export const THREAD_STORAGE_URL = CLOUD_STORAGE_URL; // Redirected to cloud-storage
export const THREAD_STORAGE_API_KEY = process.env.NEXT_PUBLIC_CLOUD_STORAGE_API_KEY || '';

// @deprecated Static file server is no longer used for messaging attachments
export const STATIC_FILE_SERVER = 'https://staticfileserver.totalaccess.co.za';
export const UPLOAD_API_KEY = ''; // Deprecated — was a hardcoded legacy key; use STATIC_FILE_SERVER_API_KEY from '@/lib/staticFileServerUrl'

